import type { Probe } from '../engine/types';
import { ok } from '../engine/runner';
import { registerProbe } from '../engine/registry';

const QUERIES = [
  'prefers-color-scheme: dark',
  'prefers-reduced-motion: reduce',
  'prefers-reduced-transparency: reduce',
  'prefers-contrast: more',
  'forced-colors: active',
  'hover: hover',
  'any-hover: hover',
  'pointer: fine',
  'any-pointer: fine',
  'dynamic-range: high',
  'color-gamut: p3',
  'inverted-colors: inverted',
  'monochrome: 0',
  'scripting: enabled',
  'update: fast',
] as const;

/** The standard media-query capability matrix. Stable across visits. */
export const mediaQueriesProbe: Probe = {
  id: 'mediaQueries',
  label: 'Media queries',
  layer: 'browser',
  tier: 'client',
  category: 'browser',
  description: 'Display, input, and preference media-query matrix',
  budgetMs: 500,

  async run() {
    const results: Record<string, boolean> = {};
    for (const q of QUERIES) {
      try {
        results[q] = window.matchMedia(q).matches;
      } catch {
        results[q] = false;
      }
    }
    // Also capture coarse/pointer variants that distinguish input hardware.
    const pointerMatches = ['none', 'coarse', 'fine'].filter((v) =>
      window.matchMedia(`(pointer: ${v})`).matches,
    );
    const hoverMatches = ['none', 'hover'].filter((v) => window.matchMedia(`(hover: ${v})`).matches);

    return {
      signals: {
        matrix: ok(results),
        pointerVariants: ok(pointerMatches),
        hoverVariants: ok(hoverMatches),
      },
      parts: {
        // Preference bits (reduced-motion, contrast, color scheme) identify a
        // user; input bits (pointer/hover) identify hardware. Hash both.
        mediaQueries: { ...results, pointerVariants: pointerMatches, hoverVariants: hoverMatches },
      },
    };
  },
};

registerProbe(mediaQueriesProbe);
